import { useMemo, useCallback, useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import TranscribeModal from './Transcribe';
import type { TranslationSource } from '@/utils/documentTranslation';

import DataTable, { TypeColumn, FileMenu } from '@/components/DataTable';
import { CardContainer } from '@/layout/Card';
import { Box, Flex, LoadingPage } from '@/layout';
import Loading from '@/components/UI/Loading';

import { convertToFileSize, convertToData, convertLink } from '@/utils/convert';
import { openStorageDownload } from '@/utils/storageDownload';
import { errorHandler } from '@/utils/toastHandler';
import TranslateModal from './Translate';
import { Delete, Documents, Download, Folder, Translate } from '@/components/Icon';
import { theme } from '@/theme/Theme';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import useCurrentLang from '@/hooks/useCurrentLang';
import { createShareLink } from '@/api/document.api';
import { saveFolderZip } from './folderZipDownload';
import ShareByLinkModal from './ShareByLink';
import DeleteConfirm from './DeleteConfirm';
import ShareMenuModal from './ShareMenu';
import ShareToEmailModal from './ShareToEmail';
import AddNewFolder from './AddNewFolder';
import RenameModal from './ReName';
import MoveToModal from './MoveTo';
import BatchMoveModal from './MoveTo/BatchMoveModal';
import type { MoveDocument } from './MoveTo/batchMove';
import { Checkbox } from '@mui/material';

type DataRow = FolderOrFileItem & {
  action: string;
  added: boolean;
  rawSize?: number;
  id?: string;
};
interface FilesProps {
  breadCrumbList: { id: number | null; name: string }[];
  data: FolderOrFileItem[];
  getDocuments: (id: number | null, propagateError?: boolean) => Promise<void>;
  previousFolderId: number | null;
  currentFolderId: number | null;
  setCurrentFolderId: Dispatch<SetStateAction<number | null>>;
  setPreviousFolderId: Dispatch<SetStateAction<number | null>>;
  updateBreadcrumb: (folderId: number | null, name: string) => void;
}

const DocumentList = ({
  breadCrumbList,
  updateBreadcrumb,
  data,
  getDocuments,
  previousFolderId,
  currentFolderId,
  setCurrentFolderId,
  setPreviousFolderId,
}: FilesProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const tabLabel = t('pages.dealDetail.manageFiles.myFolder.table.columns', { returnObjects: true });
  const route = window.location.origin;
  const [docLibrary, setDocLibrary] = useState<DataRow[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const zipInFlight = useRef(false);
  const [zipFolderId, setZipFolderId] = useState<string | null>(null);

  const downloadZip = async (id: string, name: string) => {
    if (zipInFlight.current) return;
    zipInFlight.current = true;
    setZipFolderId(id);
    try {
      await saveFolderZip(Number(id), name);
    } catch (error: any) {
      errorHandler(t(error?.response?.status === 413 ? 'pages.docLibrary.zipLimit' : 'pages.docLibrary.zipFailed'));
    } finally { zipInFlight.current = false; setZipFolderId(null); }
  };

  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [showTranscribe, setShowTranscribe] = useState<boolean>(false);
  const [showTranslate, setShowTranslate] = useState<boolean>(false);
  const [translationSource, setTranslationSource] = useState<TranslationSource>();
  const [showAddNewFolderModal, setShowAddNewFolderModal] = useState<boolean>(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState<boolean>(false);
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState<boolean>(false);
  const [showShareMenuModal, setShowShareMenuModal] = useState<boolean>(false);
  const [showShareToEmailModal, setShowShareToEmailModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [showShareByLinkModal, setShowShareByLinkModal] = useState<boolean>(false);
  const [currentFolderName, setCurrentFolderName] = useState<string>('');
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [selectedFileId, setSelectedFileId] = useState<string>('');
  const [moveSelection, setMoveSelection] = useState<string[]>([]);
  const [batchDocuments, setBatchDocuments] = useState<MoveDocument[] | null>(null);

  useEffect(() => { setMoveSelection([]); }, [currentFolderId]);
  useEffect(() => {
    const visibleIds = new Set(data.filter(item => item.type !== 'folder').map(item => String(item.id)));
    setMoveSelection(previous => previous.filter(id => visibleIds.has(id)));
  }, [data]);
  const [targetId, setTargetId] = useState<number>(0);
  const [emailShareTargetName, setEmailShareTargetName] = useState('');
  const [selectedTargetType, setSelectedTargetType] = useState<string>('');
  const [shareLink, setShareLink] = useState<string>('');

  const initData = useCallback(
    async (fileAry: FolderOrFileItem[]) => {
      if (!fileAry) return;
      try {
        // 在mapping完後檢測如果currentFolderId不為null, 則新增一個name為返回上層的資料夾
        const doc: DataRow[] = fileAry.map(
          (data): DataRow => ({
            id: data.id.toString(),
            name: data.name,
            type: data.type,
            extension: data.extension,
            rawSize: Number(data.size),
            path: data.path,
            size: convertToFileSize(Number(data.size)),
            updated_at: convertToData(data.updated_at),
            created_at: data.created_at,
            action: data.path,
            added: false,
          })
        );

        if (currentFolderId !== null) {
          doc.unshift({
            id:
              breadCrumbList[breadCrumbList.length - 2] && breadCrumbList[breadCrumbList.length - 2].id != null
                ? breadCrumbList[breadCrumbList.length - 2].id!.toString()
                : '',
            name: '..',
            type: 'folder',
            path: '',
            size: '-',
            updated_at: '-',
            created_at: '',
            action: '',
            added: false,
          });
        }

        setDocLibrary(doc);
        setIsLoading(false);
      } catch (error) {}
    },
    [data, currentFolderId]
  );

  useEffect(() => {
    initData(data);
  }, [initData, data]);

  const getFolderContents = async (folderId: string, name: string) => {
    const convertFolderId = folderId === '' ? null : Number(folderId);
    setPreviousFolderId(currentFolderId);
    setCurrentFolderId(convertFolderId);
    updateBreadcrumb(folderId ? Number(folderId) : null, name);
    getDocuments(convertFolderId);
  };

  const createLink = async (target_id: number, type: string) => {
    try {
      const rawData: CreateShareLinkRequest = {
        target_type: type === 'folder' ? 'folder' : 'document',
        target_id: target_id,
      };

      const res = (await createShareLink(rawData)).data;
      if (res.success) {
        // 取得當前主網域 + /:lang/share/ + res.code
        const link = `${route}/${lang}/share/${res.data.code}`;
        setShareLink(link);
        setShowShareByLinkModal(true);
      }
    } catch (error) {
      console.error('Error creating share link:', error);
    }
  };

  const subMenuItems = useCallback(
    ({ name, file_id, type }: { name: string; file_id: string; type: string }): DataTableMenuItems[] =>
      [
        {
          name: String(t('pages.docLibrary.fileMenu.shareToUser')),
          onClick: async () => {
            setSelectedTargetType(type);
            setTargetId(Number(file_id));
            setShowShareMenuModal(true);
          },
        },
        {
          name: String(t('documentEmailShare.title')),
          onClick: () => {
            setEmailShareTargetName(name);
            setSelectedTargetType(type);
            setTargetId(Number(file_id));
            setShowShareToEmailModal(true);
          },
        },
        {
          name: String(t('pages.docLibrary.fileMenu.shareByLink')),
          onClick: async () => {
            createLink(Number(file_id), type);
          },
        },
        type !== 'folder' && {
          name: String(t('pages.docLibrary.fileMenu.moveTo')),
          onClick: async () => {
            setShowMoveToFolderModal(true);
            setSelectedFileId(file_id);
          },
        },
        // The rename API only accepts folder IDs.
        type === 'folder' && {
          name: String(t('pages.docLibrary.fileMenu.rename')),
          onClick: () => {
            setShowRenameFolderModal(true);
            setCurrentFolderName(name);
            setSelectedFolderId(file_id);
          },
        },
        {
          name: String(t(type === 'folder' ? 'pages.docLibrary.fileMenu.deleteFolder' : 'pages.docLibrary.fileMenu.deleteFile')),
          onClick: async () => {
            setSelectedTargetType(type);
            setTargetId(Number(file_id));
            setShowDeleteConfirmModal(true);
          },
        },
      ].filter(Boolean) as DataTableMenuItems[],
    [t]
  );

  const columns: ColumnDef<DataRow>[] = useMemo(
    () => [
      {
        id: 'moveSelection',
        enableSorting: false,
        header: ({ table }) => {
          const ids = table.getRowModel().rows.filter(row => row.original.type !== 'folder').map(row => row.original.id);
          const selected = ids.filter(id => moveSelection.includes(id));
          return <Checkbox inputProps={{ 'aria-label': t('pages.docLibrary.batchMove.selectPage') }}
            checked={ids.length > 0 && selected.length === ids.length}
            indeterminate={selected.length > 0 && selected.length < ids.length}
            disabled={!ids.length}
            onChange={(_, checked) => setMoveSelection(previous => checked
              ? Array.from(new Set([...previous, ...ids])) : previous.filter(id => !ids.includes(id)))} />;
        },
        cell: ({ row }) => row.original.type === 'folder' ? null : <Checkbox
          inputProps={{ 'aria-label': t('pages.docLibrary.batchMove.selectFile', { name: row.original.name }) }}
          checked={moveSelection.includes(row.original.id)}
          onChange={(_, checked) => setMoveSelection(previous => checked
            ? [...previous, row.original.id] : previous.filter(id => id !== row.original.id))} />,
      },
      {
        accessorKey: 'name',
        header: tabLabel[0],
        cell: ({ row }) => (
          <>
            {row.original.type === 'folder' ? (
              row.original.name === '..' ? (
                <Flex gridGap={'10px'} onClick={() => getFolderContents(row.original.id, row.original.name)}>
                  <span style={{ transform: 'rotateY(180deg)' }}>
                    <Folder.ReturnSVG />
                  </span>
                  <Text color={theme.colors.primary500} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    {row.original.name}
                  </Text>
                </Flex>
              ) : (
                <Flex gridGap={'10px'} onClick={() => getFolderContents(row.original.id, row.original.name)}>
                  <Folder.FolderSVG />
                  <Text color={theme.colors.primary500} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    {row.original.name}
                  </Text>
                </Flex>
              )
            ) : (
              <Flex gridGap={'10px'}>
                <Documents.DocumentsSVG fill={theme.colors.primary500} />
                <Text>{row.original.name}</Text>
              </Flex>
            )}
          </>
        ),
      },
      {
        accessorKey: 'type',
        header: tabLabel[1],
        cell: ({ row }) => <>{row.original.type === 'folder' ? '-' : <TypeColumn text={row.original.type} />}</>,
      },
      {
        accessorKey: 'size',
        header: tabLabel[2],
        cell: ({ row }) => <>{row.original.type === 'folder' ? '-' : row.original.size}</>,
      },
      {
        accessorKey: 'updated_at',
        header: tabLabel[3],
      },
      {
        accessorKey: 'action',
        header: tabLabel[4],
        cell: ({ row }) => {
          if (row.original.name === '..') {
            return null;
          }
          const { id, type, path } = row.original;
          //   let added = hasMatchingProps(docLibrary, fileList, 'id', 'document_library_id');
          return (
            <Flex>
              {type !== 'folder' && (
                <Button
                  size="sm"
                  type="button"
                  aria-label={t('pages.docLibrary.translateFile')}
                  onClick={async () => {
                    if (!id) return;
                    setCurrentDocumentId(id);
                    setTranslationSource({ name: row.original.name, extension: row.original.extension || (type === 'file' ? undefined : type), size: row.original.rawSize });
                    setShowTranslate(true);
                  }}
                >
                  <Translate.TranslateSVG />
                </Button>
              )}
              {type === 'folder' && (
                <Button type="button" size="sm" disabled={zipFolderId !== null} onClick={() => downloadZip(id, row.original.name)}
                  aria-label={t(zipFolderId === id ? 'pages.docLibrary.zipPreparing' : 'pages.docLibrary.downloadZip')}>
                  {t(zipFolderId === id ? 'pages.docLibrary.zipPreparing' : 'pages.docLibrary.downloadZip')}
                </Button>
              )}
              {type !== 'folder' && (
                <Button
                  aria-label={t('buttons.download')}
                  size="sm"
                  type="button"
                  onClick={async () => {
                    if (!path) return;
                    try {
                      await openStorageDownload(path);
                    } catch {
                      errorHandler(t('pages.docLibrary.downloadFailed'));
                    }
                  }}
                >
                  <Download.DownloadSVG />
                </Button>
              )}
              <FileMenu
                path={row.original.path}
                fileType={row.original.type}
                items={[]}
                subItems={subMenuItems({
                  name: row.original.name,
                  file_id: row.original.id,
                  type: row.original.type,
                })}
              />
            </Flex>
          );
        },
      },
    ],
    [t, tabLabel, lang, zipFolderId, moveSelection]
  );

  return (
    <CardContainer padding={'30px 0'}>
      <LoadingPage isLoading={isLoading} />
      <ShareByLinkModal
        show={showShareByLinkModal}
        setShowShareByLinkModal={setShowShareByLinkModal}
        shareLink={shareLink}
      />
      <DeleteConfirm
        show={showDeleteConfirmModal}
        document_id={targetId}
        setDeleteModal={setShowDeleteConfirmModal}
        getDocuments={getDocuments}
        currentFolderId={currentFolderId}
        type={selectedTargetType}
      />
      <ShareMenuModal
        show={showShareMenuModal}
        setShowShareMenuModal={setShowShareMenuModal}
        targetId={targetId}
        type={selectedTargetType}
      />
      <ShareToEmailModal show={showShareToEmailModal} onClose={() => setShowShareToEmailModal(false)}
        targetId={targetId} targetType={selectedTargetType === 'folder' ? 'folder' : 'document'} targetName={emailShareTargetName} />
      <AddNewFolder
        show={showAddNewFolderModal}
        setShowAddNewFolderModal={setShowAddNewFolderModal}
        previousFolderId={previousFolderId}
        currentFolderId={currentFolderId}
        getDocuments={getDocuments}
      />
      <RenameModal
        show={showRenameFolderModal}
        setShowRenameFolderModal={setShowRenameFolderModal}
        folderId={selectedFolderId}
        folderName={currentFolderName}
        currentFolderId={currentFolderId}
        getDocuments={getDocuments}
      />
      {batchDocuments && <BatchMoveModal documents={batchDocuments} currentFolderId={currentFolderId}
        onClose={() => setBatchDocuments(null)}
        onMoved={async (ids) => {
          setMoveSelection(previous => previous.filter(id => !ids.includes(id)));
          await getDocuments(currentFolderId, true);
        }} />}
      <MoveToModal
        show={showMoveToFolderModal}
        setShowMoveToFolderModal={setShowMoveToFolderModal}
        fileId={selectedFileId}
        currentFolderId={currentFolderId}
        getDocuments={getDocuments}
      />
      <TranscribeModal
        show={showTranscribe}
        setShowTranscribe={setShowTranscribe}
        document_id={currentDocumentId}
        getDocuments={getDocuments}
      />
      <TranslateModal
        source={translationSource}
        show={showTranslate}
        setShowTranslate={setShowTranslate}
        document_id={currentDocumentId}
        getDocuments={getDocuments}
        refreshDocuments={() => getDocuments(currentFolderId)}
      />
      {breadCrumbList.length < 3 && (
        <Button
          variant={'primary'}
          marginBottom={'15px'}
          rounded={false}
          onClick={() => setShowAddNewFolderModal(true)}
        >
          {t('pages.docLibrary.addFolder')}
        </Button>
      )}
      <Button type="button" variant="primary-invert" marginBottom="15px" disabled={!moveSelection.length}
        onClick={() => setBatchDocuments(data.filter(item => item.type !== 'folder' && moveSelection.includes(String(item.id)))
          .map(item => ({ id: String(item.id), name: item.name })))}>
        {t('pages.docLibrary.batchMove.title', { count: moveSelection.length })}
      </Button>
      {docLibrary ? <DataTable columns={columns} data={docLibrary} canSearch /> : <Loading />}
    </CardContainer>
  );
};

export default DocumentList;
